import _, { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ToastComponent from '../components/toast';
import { selectToken } from '../pages/auth/authSlice';
import { type ApiError, OpenAPI } from '../services/wealthlane-tax-services';
import { useLocation } from 'react-router-dom';
import { selectTenantId } from '../pages/configuration/customerSwitchUserSlice';
import useInstance from './custom-hooks/useInstance';
import { attachDecimalConfigToRequest } from '../utils/apiDecimalConfig';
import { showErrorModal } from '../components/error-modal/errorModalEmitter';

export function useWealthlaneTaxApi(): {
  dismissError: () => void;
  error: ApiError | undefined;
  isLoading: boolean;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
} {
  const token = useSelector(selectToken);

  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { getRoleId, getTenantById } = useInstance();

  OpenAPI.BASE = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
  OpenAPI.TOKEN = token?.toString();
  // OpenAPI.HEADERS = {
  //   [`X-Tenant-Id`]: tenantId === null ? '' : (tenantId as string),
  //   ['X-Switch-Source-Role-Id']: window.localStorage.getItem('x-switch-role-id') ?? '',
  // };
  OpenAPI.HEADERS = {
    [`X-Tenant-Id`]: getTenantById()?.tenantId ?? '',
    ['X-Switch-Source-Role-Id']: getRoleId() ?? '',
  };

  const location = useLocation();

  const handleRequest = useCallback(async function <T>(request: Promise<T>) {
    // debugger;

    setIsLoading(true);

    // Transfer decimal config metadata from promise to axios interceptor
    attachDecimalConfigToRequest(request);

    try {
      const response = await request;
      setError(undefined);
      return response;
    } catch (exception: any) {
      setError(exception);
      if (!location?.pathname?.includes('tax-library')) {
        if (
          exception?.body?.error?.validationErrors != null &&
          exception?.body?.error?.validationErrors !== false
        ) {
          const error = exception?.body?.error?.validationErrors?.map((error: any) => {
            return error.message;
          });

          if (
            error.includes('OTP is invalid') === true ||
            error.includes('Invalid Template') === true ||
            error.includes('Only excel file can be uploaded') === true
          ) {
            //
          } else {
            toast.error(<ToastComponent label='Error' message={`${String(error ?? '')}`} />, {
              toastId: 1,
              autoClose: 30000,
            });
          }
        } else if (
          exception?.body?.error?.message.includes('Index was outside the bounds of the array.') ===
          true
        ) {
          toast.error(<ToastComponent label='Error' message={'Invalid data format'} />, {
            autoClose: 30000,
          });
        } else if (exception?.body?.error != null) {
          toast.error(
            <ToastComponent
              label='Error'
              message={`${String(exception?.body?.error?.message ?? '')}`}
            />,
            { autoClose: 30000 }
          );
        } else if (
          exception.status === 401 ||
          (exception.status === 403 && isEmpty(exception?.body))
        ) {
          /* empty */
        } else if (!_.isEmpty(exception?.body?.error?.message)) {
          toast.error(
            <ToastComponent
              label='Error'
              message={`${String(exception?.body?.error?.message ?? '')}`}
            />,
            {
              autoClose: 30000,
            }
          );
        } else {
          // toast.error(
          //   <ToastComponent
          //     label='Error'
          //     message={'Server is down at the moment. Please try again after sometime.'}
          //   />,
          //   {
          //     toastId: 2,
          //   }
          // );
          showErrorModal({ status: exception?.status });
        }
      } else if (
        exception?.body?.error?.message.includes('Index was outside the bounds of the array.') ===
        true
      ) {
        toast.error(<ToastComponent label='Error' message={'Invalid data format'} />, {
          autoClose: 30000,
        });
      } else if (
        exception?.body?.error?.message.includes(
          'An internal error occurred during your request!'
        ) === true
      ) {
      } else if (exception?.body?.error != null) {
        toast.error(
          <ToastComponent
            label='Error'
            message={`${String(exception?.body?.error?.message ?? '')}`}
          />,
          { autoClose: 30000 }
        );
      } else if (
        exception.status === 401 ||
        (exception.status === 403 && isEmpty(exception?.body))
      ) {
        /* empty */
      } else if (!_.isEmpty(exception?.body?.error?.message)) {
        toast.error(
          <ToastComponent
            label='Error'
            message={`${String(exception?.body?.error?.message ?? '')}`}
          />,
          {
            autoClose: 30000,
          }
        );
      } else {
        showErrorModal({ status: exception?.status });
      }
      throw Error(exception);
    } finally {
      setIsLoading(false);
    }
  }, []);

  function dismissError(): void {
    setError(undefined);
  }

  return { dismissError, error, isLoading, handleRequest };
}

export default useWealthlaneTaxApi;
